import {
  Brush,
  Wand2,
  Film,
  ImagePlay,
  Code2,
  Layers,
  Radio,
  Cpu,
  Zap,
} from 'lucide-react';
import type { FeatureGroup } from '../types';

export const texturesGroup: FeatureGroup = {
  id: 'textures',
  label: 'Textures & Geometry Scripts',
  headline: 'Procedural, image-based, and JS-generated geometry',
  summary:
    'Go beyond solid colors. Write JavaScript to generate textures with the Canvas 2D API, apply them to specific PBR channels or individual faces, use SVG and PNG images directly, or run a JS script to produce any 2D profile — smooth Bézier curves, data-driven outlines, dynamic labels — and extrude it straight into 3D geometry.',
  accentClass: 'from-fuchsia-500 to-pink-400',
  items: [
    {
      icon: Brush,
      title: 'JS Canvas Textures',
      description:
        'Write a JavaScript texture script that draws on an HTML Canvas using the full 2D API — gradients, patterns, noise, custom fonts, arcs, and anything else the browser supports.',
      tier: 'major',
    },
    {
      icon: Wand2,
      title: 'Parameter-Driven Textures',
      description:
        'Hook texture scripts up to the same parameter sliders as your model. Change a color, scale, or noise seed and watch the texture update live — the live params object is always up to date.',
      tier: 'major',
    },
    {
      icon: Film,
      title: 'Animated Textures',
      description:
        'Texture scripts can animate over time using requestAnimationFrame — useful for visualising dynamic surfaces, patterned light, or motion effects directly in the 3D preview.',
      tier: 'major',
    },
    {
      icon: ImagePlay,
      title: 'SVG & PNG Image Textures',
      description:
        'Use SVG and PNG image files directly as surface textures. Attach them to your model via the file system and reference them in code — no baking required.',
      tier: 'major',
    },
    {
      icon: Cpu,
      title: 'Custom GLSL Shaders',
      description:
        'Upload a .js file exporting vertexShader and fragmentShader GLSL strings and call shader_material() to apply fully custom GPU rendering to any geometry. Add a tick() callback for per-frame animation and uniforms for live parameter binding.',
      tier: 'major',
    },
    {
      icon: Code2,
      title: 'JS SVG Geometry Scripts',
      description:
        'Upload a .js file and call js_svg() in your OpenSCAD code. The script runs before compilation, generates an SVG with the full power of JavaScript — smooth Bézier curves, data-driven shapes, anything SVG path syntax supports — and the result is extruded into real 3D geometry.',
      tier: 'major',
    },
    {
      icon: Layers,
      title: 'PBR Texture Channels',
      description:
        'Apply a JS canvas texture to any PBR channel — color, roughness, metalness, normal, alpha, emissive, or ambient occlusion — by passing channel= to texture_script(). Stack multiple scripts for different channels on the same object.',
      tier: 'minor',
    },
    {
      icon: Radio,
      title: 'Face-Specific Textures',
      description:
        'Target a texture to a single face of an object (top, bottom, front, back, left, right) with the face= property — apply a label to the top while keeping other sides untextured.',
      tier: 'minor',
    },
    {
      icon: Zap,
      title: 'Pub/Sub for Scripts',
      description:
        'Texture and shader scripts share a real-time pub/sub channel. Use __publish__ and __subscribe__ to pass values between scripts, parameters (@pubsub), and interactive() click events — the main thread caches and delivers the last-known value to late-starting subscribers.',
      tier: 'minor',
    },
  ],
};
